import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Trash2, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getCustomers,
  getTimeEntry,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
} from "@/services/api";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

export default function TimeEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: "",
    date: format(new Date(), "yyyy-MM-dd"),
    start_time: "09:00",
    end_time: "17:00",
    description: "",
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const customersData = await getCustomers();
      setCustomers(customersData);

      if (isEditing) {
        const entryData = await getTimeEntry(id);
        setFormData({
          customer_id: entryData.customer_id,
          date: entryData.date,
          start_time: entryData.start_time,
          end_time: entryData.end_time,
          description: entryData.description || "",
        });
      } else if (customersData.length > 0) {
        setFormData((prev) => ({ ...prev, customer_id: customersData[0].id }));
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id) {
      toast.error("Bitte wähle einen Kunden aus");
      return;
    }

    if (!formData.start_time || !formData.end_time) {
      toast.error("Bitte gib Start- und Endzeit ein");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await updateTimeEntry(id, formData);
        toast.success("Zeiteintrag aktualisiert");
      } else {
        await createTimeEntry(formData);
        toast.success("Zeiteintrag erstellt");
      }
      navigate("/");
    } catch (error) {
      console.error("Error saving entry:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTimeEntry(id);
      toast.success("Zeiteintrag gelöscht");
      navigate("/");
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        date: format(date, "yyyy-MM-dd"),
      }));
      setCalendarOpen(false);
    }
  };

  const calculateDuration = () => {
    try {
      const [startH, startM] = formData.start_time.split(":").map(Number);
      const [endH, endM] = formData.end_time.split(":").map(Number);
      let minutes = (endH * 60 + endM) - (startH * 60 + startM);
      if (minutes < 0) minutes += 24 * 60;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    } catch {
      return "0h 0m";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="time-entry-loading">
        <div className="animate-pulse text-zinc-500">Laden...</div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in-up" data-testid="no-customers">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-zinc-100"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Zeit erfassen
          </h1>
        </div>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6 text-center">
            <p className="text-zinc-400 mb-4">
              Du musst zuerst einen Kunden anlegen, bevor du Zeit erfassen kannst.
            </p>
            <Button
              onClick={() => navigate("/clients")}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            >
              Kunden anlegen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="time-entry-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-zinc-400 hover:text-zinc-100"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-xl font-bold flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {isEditing ? "Eintrag bearbeiten" : "Zeit erfassen"}
        </h1>
        {isEditing && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                data-testid="delete-button"
              >
                <Trash2 size={20} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
              <AlertDialogHeader>
                <AlertDialogTitle>Eintrag löschen?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400">
                  Dieser Zeiteintrag wird unwiderruflich gelöscht.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-zinc-700 hover:bg-zinc-800">
                  Abbrechen
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete"
                >
                  Löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Customer Select */}
        <div className="form-group">
          <label className="form-label">Kunde</label>
          <Select
            value={formData.customer_id}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, customer_id: value }))
            }
          >
            <SelectTrigger
              className="h-12 bg-zinc-950 border-zinc-800 focus:border-amber-500"
              data-testid="customer-select"
            >
              <SelectValue placeholder="Kunde auswählen" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              {customers.map((customer) => (
                <SelectItem
                  key={customer.id}
                  value={customer.id}
                  className="focus:bg-zinc-800"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="customer-dot"
                      style={{ backgroundColor: customer.color }}
                    />
                    {customer.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Picker */}
        <div className="form-group">
          <label className="form-label">Datum</label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-12 justify-start text-left font-normal bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700"
                data-testid="date-picker"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                {format(parseISO(formData.date), "d. MMMM yyyy", { locale: de })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
              <Calendar
                mode="single"
                selected={parseISO(formData.date)}
                onSelect={handleDateSelect}
                locale={de}
                className="rounded-md"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time Inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label">Startzeit</label>
            <Input
              type="time"
              value={formData.start_time}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, start_time: e.target.value }))
              }
              className="h-12 bg-zinc-950 border-zinc-800 focus:border-amber-500"
              data-testid="start-time"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Endzeit</label>
            <Input
              type="time"
              value={formData.end_time}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, end_time: e.target.value }))
              }
              className="h-12 bg-zinc-950 border-zinc-800 focus:border-amber-500"
              data-testid="end-time"
            />
          </div>
        </div>

        {/* Duration Display */}
        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-amber-500 text-sm font-medium">Dauer</span>
            <span className="text-2xl font-bold text-amber-500 time-display" data-testid="duration-display">
              {calculateDuration()}
            </span>
          </CardContent>
        </Card>

        {/* Description */}
        <div className="form-group">
          <label className="form-label">Beschreibung</label>
          <Textarea
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Was hast du gemacht?"
            className="min-h-24 bg-zinc-950 border-zinc-800 focus:border-amber-500 resize-none"
            data-testid="description"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={saving}
          className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl glow-amber"
          data-testid="save-button"
        >
          {saving ? (
            "Speichern..."
          ) : (
            <>
              <Save size={18} className="mr-2" />
              {isEditing ? "Aktualisieren" : "Speichern"}
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
